import React, { useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { toastConfig } from 'assets/data/config';
import { StylishConfirmDialog } from 'components/DesignSystems/New/StylishConfirmDialog';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import IconDownload from 'assets/images/icon__download--white.svg';
import RenameFileDialog from './RenameFileDialog';
import StylishViewFileDialog from 'components/DesignSystems/New/StylishViewFileDialog';
import { useSignedUrl } from './hooks/useNotebookQueries';
import { FileOrFolder, filesOrFoldersKeys } from './hooks/useGetAllFiles';

import { useDeleteFile } from './hooks/fileMutations';

interface Props {
  file: FileOrFolder;
  onFileAction?: (actionType: string, file: FileOrFolder) => void;
}

function isRenameSuccessPayload(
  value: unknown
): value is { id: string; file_name: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof (value as { id: unknown }).id === 'string' &&
    'file_name' in value &&
    typeof (value as { file_name: unknown }).file_name === 'string'
  );
}

const FileActionsMenu: React.FC<Props> = ({ file, onFileAction }) => {
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameDialogData, setRenameDialogData] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [fileViewUrl, setFileViewUrl] = useState<string | null>(null);
  const [isLoadingView, setIsLoadingView] = useState(false);

  const queryClient = useQueryClient();
  const { mutateAsync: getSignedUrl } = useSignedUrl();
  const { mutate: deleteFile } = useDeleteFile();

  const handleRenameDialogClose = (afterSave?: unknown) => {
    setShowRenameDialog(false);
    if (isRenameSuccessPayload(afterSave) && file.workspaceId) {
      void queryClient.invalidateQueries({
        queryKey: filesOrFoldersKeys.allFiles(file.workspaceId),
        exact: false,
      });
    }
  };

  const onRenameFile = () => {
    setRenameDialogData(file);
    setShowRenameDialog(true);
    if (onFileAction) onFileAction('rename', file);
  };

  const onOpenFile = async () => {
    if (!file?.fileUrl) {
      toast.error('File URL is missing', toastConfig);
      return;
    }

    // Prevent multiple simultaneous clicks (avoids Azure SAS token clock skew errors)
    if (isLoadingView) {
      return;
    }

    // Teams + PDF: open a blank placeholder window synchronously within the user
    // gesture, before any awaits, to avoid popup blockers. We'll navigate it once
    // the signed URL is ready.
    const isInTeams = sessionStorage['isUsingTeams'] === 'true';
    const isPdf =
      !!file.file_name && file.file_name.toLowerCase().endsWith('.pdf');
    let teamsWindow: Window | null = null;
    if (isInTeams && isPdf) {
      teamsWindow = window.open('', '_blank');
    }

    setIsLoadingView(true);
    try {
      const url = await getSignedUrl(file?.fileUrl);

      // Azure SAS tokens can have a start time slightly in the future due to server
      // clock skew between COMS and Azure Blob Storage. This results in an error
      // viewing the file intermittently in Teams. Wait a fixed 1500ms to let the
      // token become valid before the browser uses it.
      // TODO: correct fix is to have COMS backdate the SAS token `st` by ~5 mins.
      // but given this is a hotfix, we'll keep this client-side workaround for now.
      if (isInTeams && isPdf) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }

      if (isInTeams && isPdf) {
        if (teamsWindow) {
          teamsWindow.location.href = url;
        } else {
          // Placeholder was blocked by the browser; best-effort fallback.
          window.open(url, '_blank');
        }
        if (onFileAction) onFileAction('open', file);
      } else {
        setFileViewUrl(url);
        setShowFileDialog(true);
        if (onFileAction) onFileAction('open', file);
      }
    } catch (_) {
      teamsWindow?.close();
      toast.error('Failed to view file', toastConfig);
    } finally {
      setIsLoadingView(false);
    }
  };

  const onDownloadFile = async () => {
    if (!file?.fileUrl) {
      toast.error('File URL is missing', toastConfig);
      return;
    }
    try {
      const url = await getSignedUrl(file?.fileUrl);
      const res = await fetch(url, { headers: {} });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = file.file_name;
      a.click();
      URL.revokeObjectURL(a.href);
      if (onFileAction) onFileAction('download', file);
    } catch (_) {
      toast.error('Failed to download file', toastConfig);
    }
  };

  const onConfirmDelete = () => {
    if (deleteId) {
      deleteFile({ fileId: deleteId, workspaceId: file.workspaceId });
      setDeleteId(null);
      if (onFileAction) onFileAction('delete', file);
    }
  };

  return (
    <>
      <Dropdown className="flex-shrink-0">
        <Dropdown.Toggle className="button--icon">
          <i className="fa fa-ellipsis-v" aria-hidden="true"></i>
        </Dropdown.Toggle>
        <Dropdown.Menu align="end">
          <Dropdown.Item onClick={onRenameFile}>
            <SharedIcon iconName="stylus" />
            Rename File
          </Dropdown.Item>
          <Dropdown.Item onClick={onDownloadFile}>
            <img src={IconDownload} alt="" />
            Download File
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setDeleteId(file.id)}>
            <SharedIcon iconName="delete" />
            Delete File
          </Dropdown.Item>
          <Dropdown.Item onClick={onOpenFile} disabled={isLoadingView}>
            <SharedIcon iconName="visibility" />
            View File
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      {showRenameDialog && (
        <RenameFileDialog
          show={showRenameDialog}
          onClose={handleRenameDialogClose}
          updateDialogData={renameDialogData}
          dialogType={''}
        />
      )}
      {showFileDialog && fileViewUrl && (
        <StylishViewFileDialog
          show={showFileDialog}
          onClose={() => setShowFileDialog(false)}
          fileUrl={fileViewUrl}
          fileName={file.file_name}
        />
      )}
      {deleteId && (
        <StylishConfirmDialog
          show={!!deleteId}
          onClose={() => setDeleteId(null)}
          dialogTitle={'Delete File'}
          dialogContent={'Are you sure you want to do this?'}
          onConfirm={onConfirmDelete}
        />
      )}
    </>
  );
};

export default FileActionsMenu;
